import moment from "moment";
import { numberOrNaN as num, toDisplayFormula, validateVersionDetails } from "pages/PayRoll/Shared/statutoryConfigData";
import { MONTHS, MONTH_OPTIONS, optionLabel } from "pages/PayRoll/Configuration/StatutoryConfiguration/LWF/lwfConfigData";

export {
  ACTIVE_STATUS_COLORS,
  VERSION_STATUS_COLORS,
  activeVersion,
  draftFrom,
  formatDay,
  formatMonth,
  isLocked,
  savedVersions,
  upcomingVersion,
  versionInForce,
  versionRange,
} from "pages/PayRoll/Shared/statutoryConfigData";
export { MONTHS, MONTH_OPTIONS, optionLabel };

// Payroll > Configuration > Statutory > Professional Tax (PT Specification
// v1.7, screens PT1–PT1d): form helpers, local checks and option lists
// (used until GET config/options/ answers). The screens load and save
// through ptConfigApi.js. PT is set up per site (work location), each site
// with its own versions.

// ---- Options ----

export const FREQUENCY_OPTIONS = [
  { value: "MONTHLY", label: "Monthly" },
  { value: "HALF_YEARLY", label: "Half-yearly" },
  { value: "YEARLY", label: "Yearly" },
];

export const COLLECT_OPTIONS = [
  { value: "ONCE_LAST_MONTH", label: "Once, in the last month" },
  { value: "MONTHLY_BALANCE_LAST", label: "Every month, balance in the last month" },
];
export const APPLIES_TO_OPTIONS = [
  { value: "ALL", label: "All employees" },
  { value: "WOMEN", label: "Women" },
  { value: "MEN", label: "Men" },
];
export const APPLIES_TO_SHORT = { ALL: "All", WOMEN: "Women", MEN: "Men" };
export const EXEMPTION_MODE_OPTIONS = [
  { value: "AUTO_AGE", label: "Automatic — from date of birth" },
  { value: "HR_MARKS", label: "HR marks the employee" },
];
export const ROUNDING_OPTIONS = [
  { value: "NEAREST", label: "Nearest rupee" },
  { value: "UP", label: "Round up" },
  { value: "DOWN", label: "Round down" },
];

const monthShort = (month) => moment().month(Number(month) - 1).format("MMM");
const addMonths = (month, count) => String(((Number(month) - 1 + count) % 12) + 1);

// "Apr – Sep · Oct – Mar" for half-years starting in April.
export const periodLabel = (frequency, startMonths) => {
  if (frequency === "HALF_YEARLY" && startMonths.length === 2) {
    return startMonths.map((start) => `${monthShort(start)} – ${monthShort(addMonths(start, 5))}`).join(" · ");
  }
  if (frequency === "YEARLY" && startMonths.length === 1) return `${monthShort(startMonths[0])} – ${monthShort(addMonths(startMonths[0], 11))}`;
  return "—";
};
// Half-years: pick the first start month; the second is 6 months later.
export const HALF_YEAR_OPTIONS = Array.from({ length: 6 }, (_, index) => {
  const first = String(index + 1);
  const second = addMonths(first, 6);
  const starts = [first, second].sort((a, b) => Number(a) - Number(b));
  return { value: starts.join(","), label: periodLabel("HALF_YEARLY", starts) };
});
export const YEAR_START_OPTIONS = MONTH_OPTIONS.map((option) => ({ value: option.value, label: periodLabel("YEARLY", [option.value]) }));

// ---- Slabs ----

let slabSeq = 100;
export const emptySlab = (overrides = {}) => {
  slabSeq += 1;
  return { id: slabSeq, wage_from: "", wage_to: "", amount: "", applies_to: "ALL", special_month_amount: "", ...overrides };
};

const isWhole = (value) => /^\d+$/.test(String(value ?? "").trim());

export const sortSlabs = (slabs) => [...slabs].sort((a, b) => num(a.wage_from) - num(b.wage_from));

// Field errors for one slab (Edit Slab drawer).
export const slabFieldErrors = (item, specialOn) => {
  const errors = {};
  if (!isWhole(item.wage_from)) errors.wage_from = "Whole rupees, ₹0 or more";
  if (item.wage_to !== "" && item.wage_to != null) {
    if (!isWhole(item.wage_to)) errors.wage_to = "Whole rupees, or leave blank for the top slab";
    else if (!errors.wage_from && num(item.wage_to) < num(item.wage_from)) errors.wage_to = "Can't be below wage from";
  }
  if (!isWhole(item.amount)) errors.amount = "Whole rupees, ₹0 or more";
  if (specialOn && !isWhole(item.special_month_amount)) errors.special_month_amount = "Enter the amount for that month";
  return errors;
};

// One Applies-to group: starts at ₹0, each slab starts the rupee after the
// previous one ends, only the top slab is open (wage_from and wage_to are
// both included).
export const groupCheck = (slabs) => {
  const sorted = sortSlabs(slabs);
  if (!sorted.length) return null;
  if (num(sorted[0].wage_from) !== 0) return "must start at ₹0";
  for (let index = 0; index < sorted.length; index += 1) {
    const current = sorted[index];
    const next = sorted[index + 1];
    const open = current.wage_to === "" || current.wage_to == null;
    if (!next) return open ? null : `top slab needs a blank “wage to” (and above)`;
    if (open) return `slab ${index + 1} has no “wage to” but is not the top slab`;
    const gap = num(next.wage_from) - num(current.wage_to);
    if (gap > 1) return `gap between ₹${num(current.wage_to).toLocaleString("en-IN")} and ₹${num(next.wage_from).toLocaleString("en-IN")}`;
    if (gap < 1) return `slabs ${index + 1} and ${index + 2} overlap`;
  }
  return null;
};

// Whole-table check (B5.8): every group used is complete; either an All set
// or both Women and Men sets.
export const slabCheck = (slabs, specialOn) => {
  if (!slabs.length) return { valid: false, message: "Add at least one slab" };
  const incomplete = sortSlabs(slabs).findIndex((item) => Object.keys(slabFieldErrors(item, specialOn)).length);
  if (incomplete >= 0) return { valid: false, message: `Slab ${incomplete + 1}: fill wage from and PT amount${specialOn ? " (and the special-month amount)" : ""}` };
  const groups = ["ALL", "WOMEN", "MEN"].filter((group) => slabs.some((item) => item.applies_to === group));
  for (const group of groups) {
    const problem = groupCheck(slabs.filter((item) => item.applies_to === group));
    if (problem) return { valid: false, message: `${APPLIES_TO_SHORT[group]} slabs: ${problem}` };
  }
  if (!groups.includes("ALL") && !(groups.includes("WOMEN") && groups.includes("MEN"))) {
    return { valid: false, message: "Add an All set, or both Women and Men sets" };
  }
  return { valid: true, message: groups.length > 1 ? `Each set (${groups.map((group) => APPLIES_TO_SHORT[group]).join(", ")}) covers every wage from ₹0 with no gaps or overlaps` : "Slabs cover every wage from ₹0 with no gaps or overlaps" };
};

// Most PT a year could reach with these slabs (top slab every deduction),
// for the yearly-limit warning.
export const yearlyMaximum = (version) => {
  const tops = ["ALL", "WOMEN", "MEN"]
    .map((group) => sortSlabs(version.slabs.filter((item) => item.applies_to === group)).slice(-1)[0])
    .filter(Boolean);
  if (!tops.length) return 0;
  return Math.max(
    ...tops.map((top) => {
      const amount = num(top.amount) || 0;
      if (version.frequency === "MONTHLY") {
        const special = version.special_month_on && isWhole(top.special_month_amount) ? num(top.special_month_amount) : amount;
        return amount * 11 + special;
      }
      return amount * (version.frequency === "HALF_YEARLY" ? 2 : 1);
    }),
  );
};

// ---- Exemptions ----

// rule_text: free text shown in the table ("Certificate", "40% or more");
// an automatic age reason shows "Age 65" instead.
let exemptionSeq = 100;
export const emptyExemption = () => {
  exemptionSeq += 1;
  return { id: exemptionSeq, reason: "", mode: "HR_MARKS", age_from: "", rule_text: "", proof_required: false, status: "Active" };
};
export const ruleProofLabel = (item) => (item.mode === "AUTO_AGE" ? `Age ${item.age_from || "–"}` : item.rule_text || "—");

// Reasons match by their text, ignoring case and extra spaces.
const reasonKey = (value) => String(value || "").trim().replace(/\s+/g, " ").toLowerCase();

export const exemptionErrors = (item, list) => {
  const errors = {};
  const reason = String(item.reason || "").trim();
  if (!reason) errors.reason = "Enter the reason";
  else if (reason.length > 100) errors.reason = "100 characters at most";
  else if (list.some((other) => other.id !== item.id && reasonKey(other.reason) === reasonKey(reason))) errors.reason = "This reason is already in the list";
  if (!item.mode) errors.mode = "Choose how the exemption applies";
  if (item.mode === "AUTO_AGE") {
    const age = Number(item.age_from);
    if (!/^\d+$/.test(String(item.age_from)) || age < 1 || age > 120) errors.age_from = "Enter the age (1 to 120)";
    if (list.some((other) => other.id !== item.id && other.mode === "AUTO_AGE")) errors.mode = "Only one automatic age exemption per site";
  }
  if (String(item.rule_text || "").trim().length > 100) errors.rule_text = "100 characters at most";
  return errors;
};

// ---- Components (B3.2) ----

// The period total: the API uses one code for half-yearly and yearly sites.
export const PERIOD_CODE = "PT_PERIOD_WAGE";
const PERIOD_NAMES = { HALF_YEARLY: "6-Month PT Wage", YEARLY: "12-Month PT Wage" };
const PERIOD_FORMULAS = { HALF_YEARLY: "PT_WAGE Apr–Sep (or Oct–Mar) added up (fixed)", YEARLY: "PT_WAGE of the 12 months added up (fixed)" };

// The period wage row follows the frequency (name and wording only); it is
// shown only for half-yearly and yearly sites.
export const syncPeriodComponent = (frequency, components) =>
  components.map((component) =>
    component.code === PERIOD_CODE && PERIOD_NAMES[frequency]
      ? { ...component, name: PERIOD_NAMES[frequency], formula: PERIOD_FORMULAS[frequency], fixed: true }
      : component,
  );
export const visibleComponents = (form) =>
  form.frequency === "HALF_YEARLY" || form.frequency === "YEARLY" ? form.components : form.components.filter((component) => component.code !== PERIOD_CODE);

export const REQUIRED_CODES = ["PT_WAGE", PERIOD_CODE, "PT"];
export const tableFormulaOf = (component) => toDisplayFormula(component.formula);

// ---- Validation (B2, B3.2; AC-C03–C06) ----

// Checked here before Save; the server checks again and its messages show
// under the same fields. State and local body come from the site
// (Administration › Sites).
export const validateSiteVersion = (form, versions) => {
  const errors = validateVersionDetails(form, versions);
  if (!form.state) errors.state = "Set the site's state in Administration › Sites";
  if (!String(form.registration_no || "").trim()) errors.registration_no = "Enter the PT registration number";
  if (!form.frequency) errors.frequency = "Choose how often PT is deducted";
  if (form.frequency === "HALF_YEARLY") {
    const [first, second] = form.period_start_months.map(Number);
    if (form.period_start_months.length !== 2 || Math.abs(second - first) !== 6) errors.period_start_months = "Half-years must cover 12 months without overlap";
  }
  if (form.frequency === "YEARLY" && form.period_start_months.length !== 1) errors.period_start_months = "Choose the month the year starts";
  if (form.frequency && form.frequency !== "MONTHLY" && !form.collect_mode) errors.collect_mode = "Choose how to collect";
  const due = String(form.payment_due_days ?? "").trim();
  if (!/^\d+$/.test(due) || Number(due) > 365) errors.payment_due_days = "Enter the days after the period ends";
  if (!(num(form.yearly_limit) > 0) || !isWhole(form.yearly_limit)) errors.yearly_limit = "Enter the most PT per employee in a year";
  if (form.special_month_on && !form.special_month) errors.special_month = "Pick the month";
  const slabs = slabCheck(form.slabs, form.special_month_on);
  if (!slabs.valid) errors.slabs = slabs.message;
  if (form.exemptions.filter((item) => item.mode === "AUTO_AGE").length > 1) errors.exemptions = "Only one automatic age exemption per site";
  return errors;
};
